import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Form, Row } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CREATE_LD_CLAIM_FORM_IDS,
  LD_CLAIM_DUPLICATE_STATUS,
  LD_CLAIM_DUPLICATE_STATUS_MESSAGE,
  LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE,
} from '../../../../../contants/ClaimsEnums';
import { APP_ROUTES } from '../../../../../contants/routes';
import {
  createLDClaimInProgress,
  setLADClaimEditDrawer,
  validateDuplicateClaimInProgress,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setAppLoader, setPageBlocker } from '../../../../../redux/common/CommonActions';
import { validateForms, valueToString } from '../../../../../utilz';
import { LADCovertDatesFromMomentToAPIType, convertPreClaimsFilesToAPI } from '../../../../../utilz/PreClaimsUtils';
import { routeFileSelector } from '../../../../../utilz/i18n';
import EmptyRecords from '../../../../components/Common/EmptyRecords';
import HiddenFormInput from '../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../components/Labels/SecondaryLabel';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import Paper from '../../../../components/Paper/Paper';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import SaveCancelButtons from '../../../Shipment/components/SaveCancelButtons';
import { useGetLADClaimInitialValues } from '../../hooks';
import CreateLDClaimTabs from './CreateLDClaimTabs';

const LossAndDamageCreateClaimScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const { selectedCompany } = useSelector((state) => state.auth);
  const {
    data: initialCreateData,
    isSuccess: isInitialCreateDataSuccess,
    isFetching: isInitialCreateDataLoading,
  } = useGetLADClaimInitialValues(selectedCompany?.companyId);

  const [activeKeys, setActiveKeys] = useState(['1', '2', '3', '4', '5', '6']);
  const isInitialRenderRef = useRef(true);

  const [items, setItems] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.ITEMS);
  const [notes, setNotes] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.NOTES);
  const [settlements, setSettlements] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
  const [files, setFiles] = useState(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.FLIES);
  const invoiceAndFbNoRef = useRef({
    invoiceNo: undefined,
    claimProNo: undefined,
  });
  const [claimsDetailsForm] = Form.useForm();
  const [claimsFinancialsForm] = Form.useForm();
  const [initialForm] = Form.useForm();
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const claimFormDataRef = useRef(null);
  const changeCompany = useSelector((state) => state.auth.changeCompany);

  useEffect(() => {
    if (changeCompany.success && !isInitialRenderRef.current) {
      claimsDetailsForm.resetFields();
      claimsFinancialsForm.resetFields();
      initialForm.resetFields();
      setItems(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.ITEMS);
      setNotes(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.NOTES);
      setSettlements(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
      setFiles(LOSS_AND_DAMAGE_CREATE_CLAIM_DEFAULT_VALUE.FLIES);
    }
    isInitialRenderRef.current = false;
  }, [changeCompany]);

  const handleFormProviderSubmit = async (submittedForm) => {
    try {
      if (submittedForm === 'saveButtonForm') {
        const formList = [
          validateForms(claimsDetailsForm, 'claimsDetailsForm'),
          validateForms(claimsFinancialsForm, 'claimsFinancialsForm'),
        ];
        await Promise.all(formList);

        if (items?.length === 1 && items?.[0]?.isNew) {
          showNotification({
            type: NOTIFICATION_TYPES.ERROR,
            message: 'Claim items required minimum one item!',
          });
          setActiveKeys(['2']);
          return false;
        }

        const payload = {
          ...claimsDetailsForm.getFieldsValue(),
          ...claimsFinancialsForm.getFieldsValue(),
          claimKey: 0,
          claimItem: items?.filter(({ editMode }) => !editMode),
          claimComment: notes?.filter(({ editMode }) => !editMode),
          claimFile: convertPreClaimsFilesToAPI(files?.filter(({ editMode }) => !editMode)),
          claimSettlement: settlements?.filter(({ editMode }) => !editMode),
        };

        dispatch(setAppLoader(true));
        const handleDone = (duplicateStatus) => {
          if (Number(duplicateStatus) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
            initiateCreateClaim(payload);
            return;
          }
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
          claimFormDataRef.current = payload;
          dispatch(setAppLoader(false));
        };
        const duplicateCheckPayload = {
          claimKey: 0,
          freightBillNo: payload.claimProNo,
          invoiceNo: payload.invoiceNo,
          handleDone,
          isInstantValidation: true,
        };
        dispatch(validateDuplicateClaimInProgress(duplicateCheckPayload));
      }
    } catch (error) {
      dispatch(setAppLoader(false));
      setActiveKeys([CREATE_LD_CLAIM_FORM_IDS[error?.formName]]);
    }
  };

  const initiateCreateClaim = (payload) => {
    payload.handleDone = () => {
      dispatch(setPageBlocker(false));
      setTimeout(() => {
        navigate(APP_ROUTES.LOSS_AND_DAMAGE_CLAIMS);
        dispatch(setLADClaimEditDrawer(true));
      }, 500);
    };
    const finalPayload = LADCovertDatesFromMomentToAPIType(payload);
    dispatch(createLDClaimInProgress(finalPayload));
  };

  const handleDuplicateApprove = () => {
    if (!isInstantDuplicateValidation) initiateCreateClaim(claimFormDataRef.current);
    handleDuplicateCancel();
  };

  const handleDuplicateCancel = () => {
    setDuplicateMessage('');
    setIsInstantDuplicateValidation(false);
    claimFormDataRef.current = null;
  };

  const checkInstantClaimsDuplicate = (changedFieldName) => async () => {
    try {
      const basicDetails = claimsDetailsForm.getFieldsValue();
      if (invoiceAndFbNoRef.current[changedFieldName] === basicDetails[changedFieldName]) {
        return;
        // biome-ignore lint/style/noUselessElse: else part will execute if having different values
      } else {
        invoiceAndFbNoRef.current = {
          invoiceNo: basicDetails.invoiceNo,
          claimProNo: basicDetails.claimProNo,
        };
      }

      const handleDone = (duplicateStatus) => {
        if (Number(duplicateStatus) !== LD_CLAIM_DUPLICATE_STATUS.NEW) {
          setIsInstantDuplicateValidation(true);
          setDuplicateMessage(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[duplicateStatus]);
        } else {
          setIsInstantDuplicateValidation(false);
        }
      };
      const payload = {
        claimKey: 0,
        freightBillNo: valueToString(basicDetails.claimProNo),
        invoiceNo: valueToString(basicDetails.invoiceNo),
        handleDone,
        isInstantValidation: true,
      };
      dispatch(validateDuplicateClaimInProgress(payload));
    } catch (error) {
      console.log(error);
      setIsInstantDuplicateValidation(false);
    }
  };

  return (
    <>
      <Form.Provider className="overflow-hidden h-100" onFormFinish={handleFormProviderSubmit}>
        <Row className="flex-direction-column flex-nowrap overflow-hidden h-100">
          <Col className="mb-15">
            <Paper className="p-15 border-radius-bottom-none">
              <Row align="middle" justify="space-between">
                <Col>
                  <Form name="initialForm" form={initialForm}>
                    <Row gutter={15}>
                      <Col>
                        <FormLabel>Claim No: </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">0</SecondaryLabel>
                        <HiddenFormInput name="shipmentNo" />
                      </Col>
                    </Row>
                  </Form>
                </Col>
                <Col>
                  <Row align="middle" gutter={15}>
                    <Col>
                      <Form name="saveButtonForm">
                        <SaveCancelButtons
                          saveButtonText={t('translation:default.buttons.save')}
                          isNew
                          onCancel={() => navigate(-1)}
                          idSuffix="shipment"
                        />
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col className="flex-1 custom-scroll-bar">
            {isInitialCreateDataLoading ? (
              <ContentSpinner size="large" />
            ) : (
              <>
                {isInitialCreateDataSuccess ? (
                  <CreateLDClaimTabs
                    activeKeys={activeKeys}
                    setActiveKeys={setActiveKeys}
                    claimsDetailsForm={claimsDetailsForm}
                    items={items}
                    setItems={setItems}
                    notes={notes}
                    setNotes={setNotes}
                    settlements={settlements}
                    setSettlements={setSettlements}
                    files={files}
                    setFiles={setFiles}
                    claimsFinancialsForm={claimsFinancialsForm}
                    checkInstantClaimsDuplicate={checkInstantClaimsDuplicate}
                    initValues={initialCreateData}
                  />
                ) : (
                  <EmptyRecords descriptionOne=" " descriptionTwo=" " title="Failed to load claims page!" />
                )}
              </>
            )}
          </Col>
        </Row>
      </Form.Provider>
      <ConfirmationModal
        open={Boolean(duplicateMessage)}
        onClose={handleDuplicateCancel}
        onOk={handleDuplicateApprove}
        onCancel={handleDuplicateCancel}
        description={duplicateMessage}
        title={t('translation:default.fields.alert')}
        cancelButton={isInstantDuplicateValidation}
      />
    </>
  );
};

export default LossAndDamageCreateClaimScreen;
